import { Col, DatePicker, Form, Input, message, Modal, Switch } from "antd";
import { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ScheduledBillings from "redux/models/scheduledBillings";
import { selectSelectedClientIds } from "redux/slices/globalSelectorSlice";
import K from "utilities/constants";
import moment from "moment";
import { ServerSideDataGrid } from "common/components/serverSideDataGrid/serverSideDataGrid";
import { selectUserId, selectUserRoles } from "redux/slices/userSlice";
import { usePageFilter } from "hooks/usePageFilters";
import { ExportDataButton } from "common/components/ExportDataButton";
import Export from "redux/models/export";
import { DataTableLayout } from "common/components/dataTableLayout/dataTableLayout";
import { columns } from "./jobColumns";

export default function JobScheduledList() {
  const userId = useSelector(selectUserId);
  const clients = useSelector(selectSelectedClientIds);
  const userRoles = useSelector(selectUserRoles);

  const gridRef = useRef(null);
  const editIdRef = useRef(null);
  const [form] = Form.useForm();

  const [search, setSearch] = usePageFilter("job_scheduled_billings_search");
  const [loading, setLoading] = useState(false);
  const [isVisible, setIsModalVisible] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  const isExportDisabled = totalCount === 0;

  const editRecord = (data) => {
    setIsModalVisible(true);
    editIdRef.current = data.id;
    form.setFieldsValue({
      duplicateAmount: data.originalDuplicateAmount,
      dueDate: moment(data.dueDate),
      isActive: data.isActive === "Yes",
    });
  };

  const tableColumns = useMemo(
    () => columns(editRecord, userRoles),
    [userRoles],
  );

  const getServerSideData = useCallback(
    (options) => {
      return ScheduledBillings.getScheduledJobBillings({
        ...options,
        client: clients,
        search: search ?? undefined,
      });
    },
    [clients, search],
  );

  const onDataFetchSuccess = useCallback(
    (data) => {
      setTotalCount(data.rowCount);
    },
    [setTotalCount],
  );

  const onDataFetchFailed = useCallback((err) => console.error(err), []);

  const onConfigUpdated = (config) => {
    gridRef.current.api.applyServerSideTransaction({
      update: [config],
    });

    gridRef.current.api.refreshCells({ force: true });
  };

  const onFinish = async (item) => {
    setLoading(true);
    const body = {
      dueDate: item.dueDate.format(K.DateFormat.Display),
      duplicateAmount: item.duplicateAmount,
      isActive: +item.isActive,
    };
    try {
      const res = await ScheduledBillings.updateJobScheduledBilling(
        body,
        editIdRef.current,
      );
      onConfigUpdated(res.data);

      setIsModalVisible(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onExportRequested = async () => {
    try {
      const { columnApi } = gridRef.current;
      const sortColumn = columnApi.getColumnState().find((c) => c.sort);

      const params = {
        client: clients,
        search: search ?? undefined,
        sortBy: sortColumn?.colId,
        sortOrder: sortColumn?.sort,
      };

      await Export.getReportScheduledJobBills(params);

      message.success(
        "Job Scheduled Billings export request successfully received! Check your email shortly",
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <>
      <DataTableLayout
        searchDefaultValue={search}
        onSearchChange={setSearch}
        actions={[
          {
            renderer: () => (
              <Col>
                <ExportDataButton
                  onClick={onExportRequested}
                  disabled={isExportDisabled}
                />
              </Col>
            ),
          },
        ]}
      >
        <ServerSideDataGrid
          ref={gridRef}
          userId={userId}
          configKey={K.AgGridTable.Keys.JobScheduledBillingsVisibleColumn}
          getData={getServerSideData}
          defaultColumnConfig={tableColumns}
          defaultColDef={K.AgGridTable.DefaultColDef}
          onDataFetchSuccess={onDataFetchSuccess}
          onDataFetchFailed={onDataFetchFailed}
        />
      </DataTableLayout>

      <Modal
        forceRender
        centered
        open={isVisible}
        closeIcon={<i className="icon-closeable" />}
        okText="Update"
        className="s2-theme-style"
        title="Edit Job Scheduled"
        wrapClassName="vertical-center-modal"
        okButtonProps={{ loading }}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        afterClose={() => form.resetFields()}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Due Date"
            name="dueDate"
            rules={[{ required: true, message: "Due Date is required." }]}
          >
            <DatePicker format={K.DateFormat.DashUSFormat} />
          </Form.Item>

          <Form.Item
            label="Amount"
            name="duplicateAmount"
            rules={[{ required: true, message: "Amount is required." }]}
          >
            <Input placeholder="Enter amount" />
          </Form.Item>
          <Form.Item label="Active?" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
